// The red moments (sprint 33 item 2), cut from the Aseprite sheets in client/art/aseprite/knocker-effects/ (built by
// art/aseprite/src/knocker_effects.lua with A2's and A3's own formulas). Nothing is drawn here: each pixel is read from the sheets.
//   creep    A2, the last 10 seconds: red Bayer from both glass edges, one frame a second from 10 s (none) to 0 s (20% a side)
//   flood    A3, the wrong Open: red Bayer over the glass, brightest at the face
//   ghost    A3, one frame: the dome again in red, 3 px off
//   cracks   A3, paper lines that start at the palms and stay
//   stamp    A3, OPENED. at 2x, tilted -7 degrees in steps, paper words on red
// The sheets are in glass px (180 x 166). Codes: 1 ink, 2 paper, 3 red, 4 dark red (index 12, never drawn as a colour of its own).
import { decodeIndexed } from './eyes.js';
import { SHEET_W, SHEET_H, BODY_LAYERS, HAND_LAYER, screamSheet, skinSheet } from './knocker.js';
import { ART } from './night.js';

export const CREEP_FRAMES = 11; // 10 s ... 0 s
const CODES = { 1: 1, 10: 2, 13: 3, 12: 4 };
const TAGS = { creep: ['creep'], lunge: ['flood', 'ghost', 'cracks', 'stamp'] };

// one tag and layer of the sheets -> frames of Uint8Array(180 * 166) codes
export function parseEffect({ w, index }, frames) {
  const out = [];
  for (let f = 0; f < frames; f++) {
    const a = new Uint8Array(SHEET_W * SHEET_H);
    for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) a[y * SHEET_W + x] = CODES[index[y * w + f * SHEET_W + x]] ?? 0;
    out.push(a);
  }
  return out;
}

const BASE = new URL('./art/aseprite/knocker-effects/', import.meta.url);
const isNode = !!globalThis.process?.versions?.node;
async function bytes(url) {
  if (isNode) { const fs = 'node:fs/promises'; return new Uint8Array(await (await import(fs)).readFile(url)); }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}
export async function loadEffects() {
  const fx = {};
  for (const [tag, layers] of Object.entries(TAGS)) {
    for (const name of layers) {
      const file = `knocker-effects-${tag}-${name}-sheet`;
      const png = await decodeIndexed(await bytes(new URL(file + '.png', BASE)));
      const frames = png.w / SHEET_W;
      fx[name] = parseEffect(png, frames);
    }
  }
  return fx;
}
let fx = null;
try { fx = await loadEffects(); } catch (e) { console.warn('red moments:', e.message); }
export const effects = () => fx;
export const setEffects = e => { fx = e; };

// the creep's sheet frame for the seconds left (10 .. 1 in play; 0 is the round's last instant): 10 s none, 0 s the widest
export const creepFrame = secsLeft => Math.max(0, Math.min(CREEP_FRAMES - 1, 10 - secsLeft));
// the lean-in: one step every 3 s (A2: 0 at 10 to 8 s, 1 at 7 to 5, 2 at 4 to 2, 3 at 1 and 0), 4% of the figure's size a step
export const leanStep = secsLeft => Math.max(0, Math.min(3, Math.floor((10 - secsLeft) / 3)));
// how much bigger the visitor stands at that step (the glass's base is 0.62 + 0.38 * approach, so 4% of the figure a step is 0.1053 of the approach); none with reduced motion
export const leanOf = (secsLeft, reduced = false) => (reduced ? 1 : 1 + 0.1053 * leanStep(secsLeft));
// a knock gets the moment: plain and gold doors only (not red, bull, bear or a lit knock), in the last ten seconds
export const lastTenOn = (special, secsLeft) => (special == null || special === 'gold') && secsLeft >= 1 && secsLeft <= 10;

const RED = 2; // bits.js T.red

// Red Bayer creep, laid over the glass's paper pixels only (never an ink pixel: the visitor and the dark glass stay as they are)
export function paintCreep(B, gx, gy, f) {
  const a = fx?.creep?.[f];
  if (!a) return 0;
  const { px, get, S } = B;
  let n = 0;
  for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) {
    const c = a[y * SHEET_W + x];
    if (c !== 3 && c !== 4) continue;
    if (!get(gx + x + S.ox, gy + y)) continue;
    S.tint = RED; px(gx + x, gy + y, 1); n++;
  }
  S.tint = 0;
  return n;
}

function paintCodes(B, gx, gy, a, pick, keep) {
  const { px, S } = B;
  let n = 0;
  for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) {
    const c = a[y * SHEET_W + x], v = pick(c);
    if (v == null || (keep && !keep(x, y))) continue;
    S.tint = v === 3 ? RED : 0; px(gx + x, gy + y, v === 1 ? 0 : 1); n++;
  }
  S.tint = 0;
  return n;
}
// the red flood: A3's red Bayer on its own under-fill, ink and dark red (dark red shows on a night door, ink on one bit), so it reads as A3's
// solid red field and not as red over the reeds; skip(x, y): a pixel to leave alone (the dome stays ink)
export function paintFlood(B, gx, gy, f, skip) {
  const a = fx?.flood?.[f];
  if (!a) return 0;
  const keep = skip && ((x, y) => !skip(x, y)), { S } = B, art0 = S.art;
  for (const [code, art] of [[1, 0], [4, ART.DARK]]) { S.art = art; paintCodes(B, gx, gy, a, c => (c === code ? 1 : null), keep); }
  S.art = 0;
  const n = paintCodes(B, gx, gy, a, c => (c === 3 ? 3 : null), keep);
  S.art = art0;
  return n;
}
export const paintGhost = (B, gx, gy, f) => (fx?.ghost?.[f] ? paintCodes(B, gx, gy, fx.ghost[f], c => (c === 3 ? 3 : null)) : 0);
export const paintCracks = (B, gx, gy, f) => (fx?.cracks?.[f] ? paintCodes(B, gx, gy, fx.cracks[f], c => (c === 2 ? 2 : null)) : 0);

// The Knocker's scream: the lunge sheet's dome, then the flood and the ghost over it, then its bloom, eyes, mouth, teeth and palms
// (the cracks come last, in drawGlass). f: the lunge sheet frame; keep(u, v): the arch. Returns false without the sheets (the old lunge stays).
// skin: one of the Knocker's stand-ins (game/fails.js), on the same frames, without the Knocker's red ghost
export function paintScream(B, gx, gy, f, keep = () => true, { flood = true, skin = null } = {}) {
  const k = skinSheet(skin) ?? screamSheet();
  if (!k || !fx?.flood) return false;
  const put = name => { const a = k.layer(name, f); if (a) paintCodes(B, gx, gy, a, c => c || null, keep); };
  put('dome');
  // the flood is round the dome as in A3: the dome keeps its ink and its paper rim
  const dome = k.layer('dome', f);
  if (flood) paintFlood(B, gx, gy, f, dome ? (x, y) => dome[y * SHEET_W + x] !== 0 : null);
  if (!skinSheet(skin)) paintGhost(B, gx, gy, f);
  for (const name of [...BODY_LAYERS.filter(n => n !== 'dome'), HAND_LAYER]) put(name);
  return true;
}

// The stamp art: the OPENED. frame's bounding box, { x, y, w, h, codes } in glass px, where A3 has it: low in the glass, under the palms (null without the sheets)
let stampBox = null, stampOf = null;
export function stampArt() {
  const a = fx?.stamp?.[6];
  if (!a) return null;
  if (stampOf === a) return stampBox;
  let x0 = SHEET_W, x1 = -1, y0 = SHEET_H, y1 = -1;
  for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) if (a[y * SHEET_W + x]) { x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
  const w = x1 - x0 + 1, h = y1 - y0 + 1, codes = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) codes[y * w + x] = a[(y0 + y) * SHEET_W + x0 + x];
  stampOf = a;
  return (stampBox = { x: x0, y: y0, w, h, codes });
}
// The scream's first sheet frame with the stamp on it: the impact frames (2 and 3) open the mouth down to row 125, over the stamp's top (row 120
// in the mouth's columns), and frame 4 touches it by a row; from frame 5 on the mouth ends at row 119, as in A3, whose stamp lands on its last frame.
export const STAMP_FROM = 5;
// paper words on red, ink shadow, in the glass whose top-left is (gx, gy)
export function paintStamp(B, gx, gy) {
  const s = stampArt();
  if (!s) return false;
  const x = gx + s.x, y = gy + s.y;
  const { px, S } = B;
  for (let j = 0; j < s.h; j++) for (let i = 0; i < s.w; i++) {
    const c = s.codes[j * s.w + i];
    if (!c) continue;
    S.tint = c === 3 ? RED : 0; px(x + i, y + j, c === 1 ? 0 : 1);
  }
  S.tint = 0;
  return true;
}

// The phone's punch-in view of the glass: 7/6 of the glass on a window 194 px wide or more, so both three-finger palms (glass columns 9 to 171) fit whole
// and the glass is 58% of the window's height; a narrower window shows it 1:1 (a whole hand or none, never a cut fingertip).
// rows: the view's height; u(x): the glass column at window column x (centred on the glass); v(y): the glass row at window row y
export function punchView(sw) {
  const n = sw >= 194 ? 7 : 1, d = sw >= 194 ? 6 : 1, mid = sw >> 1;
  return { n, d, rows: Math.round(SHEET_H * n / d), u: x => SHEET_W / 2 + Math.floor((x - mid) * d / n), v: y => Math.floor(y * d / n) };
}

// How far the shake may move the punched glass sideways before a palm leaves the window: the slack between the window's edge and the
// outermost hand pixel of any scream frame in the view (at least 0). A wider shake would cut a finger.
export function punchSlack(sw) {
  const k = screamSheet();
  if (!k) return 0;
  const v = punchView(sw);
  let lo = sw, hi = -1;
  const cols = new Map();
  for (let x = 0; x < sw; x++) { const u = v.u(x); if (!cols.has(u)) cols.set(u, [x, x]); else cols.get(u)[1] = x; }
  for (let f = 2; f < k.frames; f++) { // from the impact on: the punch-in is never on the rush frames
    const h = k.layer(HAND_LAYER, f);
    for (let y = 0; y < SHEET_H; y++) for (let u = 0; u < SHEET_W; u++) if (h[y * SHEET_W + u]) {
      const c = cols.get(u);
      if (!c) return 0;
      lo = Math.min(lo, c[0]); hi = Math.max(hi, c[1]);
    }
  }
  return hi < 0 ? 0 : Math.max(0, Math.min(lo, sw - 1 - hi));
}
// the phone's shake: whole px, the sideways part held to the slack beside the palms (vertical only past it), so neither palm is cut
export const phoneShake = ([dx, dy], sw) => { const s = punchSlack(sw); return [Math.max(-s, Math.min(s, dx)), dy]; };
